import './OrdersPage.scss'

import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import merchantApi from '../../services/merchantApi'
import Header from '../../components/Header/Header'
import DateRangeDropdown from '../../components/DateRangeDropdown/DateRangeDropdown'
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown'
import StatusFilterDropdown from '../../components/StatusFilterDropdown/StatusFilterDropdown'
import AppTable from '../../components/AppTable/AppTable'
import { Icon } from '@iconify/react/dist/iconify.js'
import { Backdrop, CircularProgress } from '@mui/material'
import moment from 'moment'
import FilterSideModal from '../../components/FilterSideModal/FilterSideModal'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

const moduleCode = 'order'

// ─── Status workflow ──────────────────────────────────────────────────────────

const STATUS_FLOW = {
  pending: { next: 'submitted', nextLabel: null },
  submitted: { next: 'confirmed', nextLabel: 'Confirm order' },
  paid: { next: 'confirmed', nextLabel: 'Confirm order' },
  confirmed: { next: 'ready', nextLabel: 'Mark as ready' },
  ready: { next: 'completed', nextLabel: 'Complete order' },
  completed: { next: null, nextLabel: null },
  cancelled: { next: null, nextLabel: null },
}

const REVERT_MAP = {
  submitted: 'pending',
  confirmed: 'paid',
  ready: 'confirmed',
  completed: 'ready',
}

const PAYMENT_LOCKED = new Set(['paid', 'cancelled'])

const CANCEL_REASONS = [
  'Item out of stock',
  'Customer requested cancellation',
  'Duplicate order',
  'Store closing early',
  'Unable to fulfil order',
  'Other',
]

// ─── Data ─────────────────────────────────────────────────────────────────────

const ORDER_TABS = [
  { value: 'all', label: 'All orders' },
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'paid', label: 'Paid' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'ready', label: 'Ready' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  // { value: 'open', label: 'Open' },
  // { value: 'closed', label: 'Closed' },
]



const unwrapList = (response) => {
  // Handle the new API response (success/message/data with data.data array)
  let payload = response
  if (payload?.data?.data && Array.isArray(payload.data.data)) {
    return payload.data.data
  }
  if (payload?.data && Array.isArray(payload.data)) {
    return payload.data
  }
  // Fallbacks for other structures
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.orders)) return payload.orders
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.records)) return payload.records
  return []
}

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const formatDateTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleTimeString('en-MY', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatStatusLabel = (status = '') => String(status)
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())

const normalizeOrderItems = (rawItems) => {
  const items = Array.isArray(rawItems) ? rawItems : []

  return items.map((item) => ({
    qty: toNumber(item.qty ?? item.quantity ?? 1, 1),
    name: item.name || item.product_name || item.title || 'Item',
    note: item.note || item.remarks || item.comment || null,
    modifiers: Array.isArray(item.modifiers)
      ? item.modifiers
      : Array.isArray(item.options)
        ? item.options
        : [],
    price: toNumber(item.price ?? item.amount ?? item.total ?? 0, 0),
  }))
}

const paymentIconFromMethod = (method = '') => {
  const value = String(method).toLowerCase()
  if (value.includes('wallet') || value.includes('ewallet') || value.includes('e-wallet')) return 'wallet'
  if (value.includes('bank') || value.includes('fpx')) return 'bank'
  return 'card'
}

const normalizeOrder = (raw = {}) => {
  const status = String(raw.status || raw.order_status || 'submitted').toLowerCase()

  // Extract totals from totals array
  const getTotalByCode = (code) => {
    const totalItem = raw.totals?.find((t) => t.code === code)
    return totalItem ? toNumber(totalItem.value, 0) : 0
  }
  const subtotal = getTotalByCode('subtotal')
  const discount = getTotalByCode('discount')
  const total = toNumber(raw.total || getTotalByCode('total'), 0)
  const serviceTax = toNumber(raw.service_charge || getTotalByCode('service_charge'), 0)
  const sst = toNumber(raw.tax || getTotalByCode('tax'), 0)

  const paymentStatus = String(raw.payment_status || raw.payment?.status || (raw.paid_at ? 'paid' : 'pending')).toLowerCase()
  const posStatus = String(raw.pos_status || raw.posState || (['completed', 'closed'].includes(status) ? 'closed' : status === 'cancelled' ? status : 'open')).toLowerCase()
  const type = raw.order_type_label || raw.order_type || raw.fulfillment_type || ''
  return {
    id: raw.order_number || raw.id || raw.order_id || '-',
    rawId: raw.id, // keep the numeric id for API calls
    pos_id: raw.pos_id || raw.posId || raw.reference_no || null,
    customer: raw.customer?.name || raw.customer_name || raw.customer || 'Guest',
    phone: raw.customer?.phone || raw.phone || raw.mobile || '-',
    points: toNumber(raw.points_earned ?? raw.points ?? 0, 0),
    orderedAt: formatDateTime(raw.ordered_at || raw.created_at || raw.order_date),
    items: normalizeOrderItems(raw.items || raw.order_items || raw.line_items),
    customerNote: raw.customer_note || raw.remarks || raw.note || null,
    subtotal,
    promocode: raw?.totals?.find((x) => x.code === 'promocode') || null,
    voucher: raw?.totals?.find((x) => x.code === 'voucher') || null,
    serviceTax: serviceTax || 0,
    sst: sst || 0,
    total,
    payment: {
      method: raw.payment?.method_name || raw.payment_method || raw.payment?.method || 'Card payment',
      icon: paymentIconFromMethod(raw.payment?.method_name || raw.payment_method || raw.payment?.method || ''),
      paidAt: formatDateTime(raw.paid_at || raw.payment?.paid_at || raw.payment?.date),
      status: paymentStatus,
    },
    type,
    placed: formatDateTime(raw.placed_at || raw.created_at || raw.order_date),
    closed: formatDateTime(raw.closed_at || raw.completed_at || raw.cancelled_at),
    status,
    payment_status: paymentStatus,
    pos_status: posStatus,
    can_cancel: raw.can_cancel || false,
    table: raw.table || null,
  }
}

const getStatusPayload = (order, nextStatus, note, remark) => {
  if (nextStatus === 'confirmed') {
    return { payload: { status: nextStatus, remark: note || undefined }, action: 'updateStatus' }
  }

  if (nextStatus === 'ready') {
    return { payload: { remark: note || undefined }, action: 'markReady' }
  }

  if (nextStatus === 'completed') {
    return { payload: { remark: note || undefined }, action: 'complete' }
  }

  if (nextStatus === 'cancelled') {
    return { payload: note ? { reason: note, remark: remark || null } : {}, action: 'cancel' }
  }

  if (nextStatus === 'revert') {
    return { payload: note ? { reason: note } : {}, action: 'revert' }
  }

  return { payload: { status: nextStatus, remark: note || undefined }, action: 'updateStatus' }
}

function PaymentIcon({ icon }) {
  if (icon === 'wallet') return <Icon icon="ph:wallet" width={20} />
  if (icon === 'bank') return <Icon icon="ph:bank" width={20} />
  return <Icon icon="ph:credit-card" width={20} />
}

// ─── Cancel Order Modal (Portal) ──────────────────────────────────────────────

function CancelOrderModal({ order, onConfirm, onClose }) {
  const [reason, setReason] = useState(CANCEL_REASONS[0])
  const [note, setNote] = useState('')

  return createPortal(
    <div className="cancel-modal-overlay" onClick={onClose}>
      <div className="cancel-modal" onClick={(e) => e.stopPropagation()}>

        <div className="cancel-modal__header">
          <h3>Cancel order {order?.id}</h3>
          <button className="cancel-modal__close" onClick={onClose}><Icon icon="ph:x" width={20} /></button>
        </div>

        <div className="cancel-modal__body">
          <div className="cancel-modal__order-info">
            <span className="cancel-modal__customer">{order?.customer}</span>
            <span className="cancel-modal__dot"><Icon icon="ph:circle-fill" height={4} color="#000" /></span>
            <span className="cancel-modal__total">RM{order?.total.toFixed(2)}</span>
          </div>
          <p className="cancel-modal__items-preview">
            {order?.items.map((i, idx) => `${i.name} x ${i.qty}`).join(', ')}
          </p>
          <div>
            <label className="cancel-modal__label">Cancellation reason</label>
            <div className="cancel-modal__select-wrapper">
              <select
                className="cancel-modal__select"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                {CANCEL_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <Icon icon="ph:caret-down" width={16} className="cancel-modal__select-caret" />
            </div>
          </div>

          <div>
            <label className="cancel-modal__label">
              Note to customer <span className="cancel-modal__optional">(optional)</span>
            </label>
            <textarea
              className="cancel-modal__textarea"
              rows={4}
              placeholder="Additional message sent with the cancellation notification"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <div className="cancel-modal__footer">
          <button className="cancel-modal__keep" onClick={onClose}>Keep order open</button>
          <button
            className="cancel-modal__confirm"
            onClick={() => { onConfirm(reason, note); onClose() }}
          >
            <Icon icon="ph:minus-circle" height={18} />
            Confirm cancellation
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Undo Toast ───────────────────────────────────────────────────────────────

function UndoToast({ toast, onUndo, onDismiss }) {
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(onDismiss, 5000)
    return () => clearTimeout(t)
  }, [toast, onDismiss])

  if (!toast) return null

  return (
    <div className="orders-toast">
      <Icon icon="ph:check-circle-fill" width={18} className="orders-toast__icon" />
      <span className="orders-toast__message">{toast.message}</span>
      {/* <button className="orders-toast__undo" onClick={onUndo}>Undo</button> */}
      <button className="orders-toast__close" onClick={onDismiss}><Icon icon="ph:x" width={16} /></button>
    </div>
  )
}

// ─── Revert Modal ─────────────────────────────────────────────────────────────

function RevertModal({ order, onConfirm, onCancel }) {
  const [reason, setReason] = useState('')
  const revertTo = REVERT_MAP[order?.status]
  if (!order || !revertTo) return null

  return (
    <div className="orders-settings-modal-overlay">
      <div className="orders-settings-modal">
        <div className="orders-settings-modal__header">
          <div>Revert order status</div>
          <button className="orders-settings-modal__close" onClick={onCancel}><Icon icon="ph:x" width={20} /></button>
        </div>
        <div className="orders-settings-modal__body">
          <div className="orders-revert-warning">
            <Icon icon="ph:warning-fill" width={18} className="orders-revert-warning__icon" />
            <p>This will revert <strong>{order?.id}</strong> from <strong>{order?.status}</strong> back to <strong>{revertTo}</strong>. This action is logged.</p>
          </div>
          <div className="orders-revert-reason">
            <label htmlFor="revert-reason">Reason <span>(required)</span></label>
            <textarea
              id="revert-reason"
              rows={3}
              placeholder="e.g. Accidentally marked as ready before food was prepared"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <div className="orders-settings-modal__footer">
          <button className="orders-settings-modal__cancel" onClick={onCancel}>Cancel</button>
          <button
            className="orders-settings-modal__save orders-settings-modal__save--danger"
            disabled={!reason.trim()}
            onClick={() => onConfirm("revert", reason.trim())}
          >
            Revert status
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

function AuditLog({ logs }) {
  if (!logs?.length) return null
  return (
    <div className="odp__section">
      <p className="odp__section-label">Activity log</p>
      <ul className="orders-audit-log">
        {logs.map((entry, i) => (
          <li key={i} className={`orders-audit-log__entry orders-audit-log__entry--${entry.type}`}>
            <span className="orders-audit-log__dot" />
            <div>
              <p className="orders-audit-log__text">{entry.message}</p>
              {entry.reason && <p className="orders-audit-log__reason">Reason: {entry.reason}</p>}
              <p className="orders-audit-log__time">{entry.time}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Status colour map ────────────────────────────────────────────────────────

const STATUS_COLOR = {
  pending: 'pending',
  submitted: 'submitted',
  paid: 'paid',
  confirmed: 'confirmed',
  ready: 'ready',
  completed: 'completed',
  cancelled: 'cancelled',
  open: 'open',
  closed: 'closed',
}

// ─── Order Status History ─────────────────────────────────────────────────────

function OrderStatusHistory({ history }) {
  if (!history?.length) return null
  return (
    <div className="odp__section">
      <p className="odp__section-label">Order status history</p>
      <div className="odp__history">
        {history.map((entry, i) => (
          <div key={entry.id} className="odp__history-row">
            {/* Timeline spine */}
            <div className="odp__history-spine">
              <span className={`odp__history-dot odp__history-dot--${STATUS_COLOR[entry.status] || 'default'}`} />
              {i < history.length - 1 && <span className="odp__history-line" />}
            </div>
            <div className="odp__history-content">
              <div className="odp__history-top">
                <span className={`odp__history-badge odp__history-badge--${STATUS_COLOR[entry.status] || 'default'}`}>
                  {entry.status}
                </span>
                <span className="odp__history-time">{entry.created_at}</span>
              </div>
              {entry.note && (
                <p className="odp__history-note">{entry.note}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── POS History ──────────────────────────────────────────────────────────────

const ACTION_LABEL = {
  ORDER_CREATED: 'Order created',
  PAYMENT_SUCCESS: 'Payment received',
  MERCHANT_ACCEPT: 'Merchant accepted',
  MARK_READY: 'Marked as ready',
  ORDER_COMPLETE: 'Order completed',
  ORDER_CANCEL: 'Order cancelled',
}

function PosHistory({ history }) {
  if (!history?.length) return null
  return (
    <div className="odp__section">
      <p className="odp__section-label">POS history</p>
      <div className="odp__history">
        {history.map((entry, i) => (
          <div key={entry.id} className="odp__history-row">
            <div className="odp__history-spine">
              <span className={`odp__history-dot odp__history-dot--${STATUS_COLOR[entry.status] || 'default'}`} />
              {i < history.length - 1 && <span className="odp__history-line" />}
            </div>
            <div className="odp__history-content">
              <div className="odp__history-top">
                <span className="odp__history-action">
                  {ACTION_LABEL[entry.action] || entry.action}
                </span>
                <span className="odp__history-time">{entry.created_at}</span>
              </div>
              <div className="odp__history-meta">
                {entry.old_status && (
                  <span className="odp__history-transition">
                    <span className={`odp__history-badge odp__history-badge--${STATUS_COLOR[entry.old_status] || 'default'}`}>
                      {entry.old_status}
                    </span>
                    <span className="odp__history-arrow">→</span>
                    <span className={`odp__history-badge odp__history-badge--${STATUS_COLOR[entry.status] || 'default'}`}>
                      {entry.status}
                    </span>
                  </span>
                )}
                {entry.ref && <span className="odp__history-ref">Ref: {entry.ref}</span>}
              </div>
              {entry.remark && <p className="odp__history-note">{entry.remark}</p>}
              {entry.system_log && <p className="odp__history-syslog">{entry.system_log}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Order Details Panel ──────────────────────────────────────────────────────

function OrderDetailsPanel({ order, auditLogs, statusHistory, posHistory, onClose, onAdvance, onRevert, onCancel, canRevert, loading, cancelable }) {
  const statusLabel = formatStatusLabel(order?.status)

  return (
    <div className='odp-overlay' onClick={onClose}>
      <div className="odp">
        {/* ── Top bar ── */}
        <div className="odp__topbar">
          <span className="odp__topbar-id">{order?.id}</span>
          <button className="odp__topbar-close" onClick={onClose}><Icon icon="ph:x" width={18} /></button>
        </div>

        <div className="odp__body">
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
              width: '100%'
            }}>
              <CircularProgress style={{ color: '#053F48' }} />
            </div>
          ) : (
            <>
              {/* ── Title row ── */}
              <div className="odp__title-row">
                <h2 className="odp__id">{order.id}</h2>
                <div className="odp__badges">
                  {statusLabel && (
                    <span className={`orders-table__status orders-table__status--${order.status}`}>
                      <Icon icon="ph:circle-fill" height={5} /> {statusLabel}
                    </span>
                  )}
                  <span className={`odp__badge odp__badge--type odp__badge--type-${order.type.toLowerCase().replace(/\s+/g, '')}`}>
                    <Icon icon="ph:circle-fill" height={5} /> {order.type ? order.type[0].toUpperCase() + order.type.slice(1) : ''}
                  </span>
                </div>
              </div>

              {/* ── Customer info ── */}
              <div className="odp__meta">
                <div className="odp__meta-row">
                  <span className="odp__meta-key">Customer:</span>
                  <span className="odp__meta-val odp__meta-val--bold">{order.customer}</span>
                </div>
                <div className="odp__meta-row">
                  <span className="odp__meta-key">Phone number:</span>
                  <span className="odp__meta-val">{order.phone}</span>
                </div>
                <div className="odp__meta-row">
                  <span className="odp__meta-key">Points earned:</span>
                  <span className="odp__meta-val">{order.points}pts</span>
                </div>
                {
                  order?.type === 'Dine In' && (
                    <div className="odp__meta-row">
                      <span className="odp__meta-key">Table:</span>
                      <span className="odp__meta-val">{order?.table?.table_name || '-'}</span>
                    </div>
                  )
                }
                <div className="odp__meta-row">
                  <span className="odp__meta-key">Ordered at:</span>
                  <span className="odp__meta-val">{order.orderedAt}</span>
                </div>
              </div>

              {/* ── Items ── */}
              <div className="odp__section">
                <p className="odp__section-label">ITEM(S)</p>
                <div className="odp__items">
                  {order.items.map((item, i) => (
                    <>
                      <div key={i} className="odp__item">
                        <div className="odp__item-left">
                          <span className="odp__item-qty">{item.qty}x</span>
                          <div>
                            <p className="odp__item-name">{item.name}</p>
                            {item.modifiers.map((el, j) => (
                              <p key={j} className="odp__item-modifier">{el?.value}</p>
                            ))}
                            {/* {item?.note && <p className="odp__item-modifier">* {item.note}</p>} */}
                          </div>
                        </div>
                        <span className="odp__item-price">{(item.price || 0).toFixed(2)}</span>
                      </div>
                      {/* Customer note */}
                      {item?.note && (
                        <div className="odp__customer-note">
                          <Icon icon={"ph:info"} height={16} className="odp__customer-note-icon" />
                          <p>
                            <strong>Customer note:</strong>
                            <div>{item.note}</div>
                          </p>
                        </div>
                      )}
                    </>
                  ))}
                </div>
              </div>

              {/* ── Pricing ── */}
              <div className="odp__pricing">
                <div className="odp__pricing-row">
                  <span>Subtotal</span>
                  <span>{(order.subtotal || 0).toFixed(2)}</span>
                </div>
                {order.voucher?.value && (
                  <div className="odp__pricing-row ">
                    <span>{'Voucher applied'} {order.voucher?.promotion?.name ? `(${order.voucher.promotion.name})` : ''}</span>
                    <span>-{Number(order.voucher.value).toFixed(2)}</span>
                  </div>
                )}
                {order.promocode?.value && (
                  <div className="odp__pricing-row ">
                    <span>{'Promocode applied'} {order.promocode?.promotion?.name ? `(${order.promocode.promotion.name})` : ''}</span>
                    <span className="odp__pricing-row--discount">-{Number(order.promocode.value).toFixed(2)}</span>
                  </div>
                )}
                <div className="odp__pricing-row">
                  <span>Service tax (10%)</span>
                  <span>{(order.serviceTax || 0).toFixed(2)}</span>
                </div>
                <div className="odp__pricing-row">
                  <span>SST (6%)</span>
                  <span>{(order.sst || 0).toFixed(2)}</span>
                </div>
                <div className="odp__divider" />
                <div className="odp__pricing-row odp__pricing-row--total">
                  <span>Total <span className="odp__incl-tax">(Incl. tax)</span></span>
                  <span>RM{(order.total || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* ── Payment ── */}
              <div className="odp__payment">
                <div className="odp__payment-icon">
                  <PaymentIcon icon={order.payment.icon} />
                </div>
                <div className="odp__payment-info">
                  <p className="odp__payment-method">{order.payment.method}</p>
                  <p className="odp__payment-date">
                    {order.payment.status === 'paid'
                      ? `Paid on  ${order.payment.paidAt}`
                      : order.payment.status === 'refunded'
                        ? `Cancelled on  ${order.payment.paidAt}`
                        : 'Awaiting payment'}
                  </p>
                </div>
                <span className={`odp__payment-badge odp__payment-badge--${order.payment.status}`}>
                  <Icon icon="ph:circle-fill" width={7} />
                  {order.payment.status === 'paid' ? 'Paid'
                    : order.payment.status === 'refunded' ? 'Refunded'
                      : 'Pending'}
                </span>
              </div>

              {/* Order status history */}
              <OrderStatusHistory history={statusHistory} />

              {/* POS history */}
              <PosHistory history={posHistory} />

              {/* In-session activity log */}
              <AuditLog logs={auditLogs} />
            </>
          )}
        </div>

        {/* ── Footer ── */}
        {!loading && (
          <div className="odp__footer">
            {(order.can_cancel && cancelable) ? (
              <button
                className="odp__footer-cancel"
                onClick={onCancel}
              >
                Cancel order
              </button>
            ) : <div></div>}

            <div className="odp__footer-actions">
              {canRevert && (
                <button className="odp__footer-revert" onClick={onRevert}>
                  <Icon icon="ph:arrow-counter-clockwise" width={16} />
                  Revert
                </button>
              )}

              {STATUS_FLOW[order.status]?.nextLabel && (
                <button className="odp__footer-primary" onClick={onAdvance}>
                  {STATUS_FLOW[order.status].nextLabel}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function OrderFilter({ open, setOpen, setValues, values, applyFilters, orderTypes = [], onReset }) {
  const filters = [
    {
      key: 'type',
      label: 'Type',
      type: 'chip',
      options: orderTypes,
    },
    {
      key: 'date_range',
      label: 'Date Range',
      type: 'chip',
      options: [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'this-week', label: 'This week' },
        { value: 'last-week', label: 'Last week' },
        { value: 'this-month', label: 'This month' },
        { value: 'last-month', label: 'Last month' },
        { value: 'custom', label: 'Custom date…' },
      ],
    },
    {
      key: 'custom_date_range',
      label: '',
      type: 'date-range',
      showWhen: {
        key: 'date_range',
        equals: 'custom',
      },
    },
    {
      key: 'order_amount_range',
      label: 'Order amount',
      type: 'number-range',
      fromLabel: 'From (RM)',
      toLabel: 'To (RM)',
      fromPlaceholder: '0',
      toPlaceholder: '100,000',
    },
  ]

  return (
    <FilterSideModal
      open={open}
      onClose={() => setOpen(false)}
      onApply={(vals) => {
        setOpen(false)
        applyFilters(vals)
      }}
      onReset={onReset ?? (() => setValues({}))}
      values={values}
      onChange={(key, val) => setValues((p) => ({ ...p, [key]: val }))}
      filters={filters}
    />
  )
}

// ─── Filter config ────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'dine-in', label: 'Dine-in' },
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'pickup', label: 'Pickup' },
]

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OrdersPage() {
  const [orders, setOrders] = useState()
  const [userInfo, setUserInfo] = useState(null)
  const [orderTypes, setOrderTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [showOrderSettings, setShowOrderSettings] = useState(false)
  const [autoAcceptOrders, setAutoAcceptOrders] = useState(true)
  const [activeTab, setActiveTab] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [meta, setMeta] = useState({})

  // Filters
  const [filterValue, setFilterValue] = useState({ custom_date_range: { start: null, end: null }, date_range: '' })
  const [filterOpen, setFilterOpen] = useState(false)
  const [customRange, setCustomRange] = useState({ start: null, end: null })
  const [filterStatus, setFilterStatus] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  const [openMenuId, setOpenMenuId] = useState(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })

  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showOrderDetails, setShowOrderDetails] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [fetchingOrderDetails, setFetchingOrderDetails] = useState(false)
  const [printingReceiptId, setPrintingReceiptId] = useState(null)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [selectedOutletId, setSelectedOutletId] = useState(null)

  const [toast, setToast] = useState(null)
  const undoSnapshot = useRef(null)

  const [showRevertModal, setShowRevertModal] = useState(false)
  const [auditLogs, setAuditLogs] = useState({})
  const [openStatus, setOpenStatus] = useState([])
  const [closedStatus, setClosedStatus] = useState([])
  const location = useLocation()
  const { state } = location
  //permission states
  const [settingViewable, setSettingViewable] = useState(true)
  const [cancelable, setCancelable] = useState(true)
  const [exportable, setExportable] = useState(true)

  const { id } = useParams()
  const navigate = useNavigate()
  console.log("customer id", state)
  const loadOrders = async ({ silent = false } = {}) => {

    try {
      if (!silent) setLoading(true)

      // Build params object
      const params = {
        page: currentPage,
        size: 10,
        "filter[status][]": filterStatus.size > 0 ? Array.from(filterStatus) : undefined,
        "filter[order_type]": filterValue?.type === 'all' ? undefined : filterValue?.type,
        "filter[date_range]": filterValue?.date_range === 'custom' || filterValue?.date_range === 'all' ? undefined : filterValue?.date_range,
        "filter[start_date]": filterValue?.custom_date_range?.start ? moment(filterValue.custom_date_range.start).format('YYYY-MM-DD') : undefined,
        "filter[end_date]": filterValue?.custom_date_range?.end ? moment(filterValue.custom_date_range.end).format('YYYY-MM-DD') : undefined,
        "filter[order_amount_from]": filterValue?.order_amount_range?.from,
        "filter[order_amount_to]": filterValue?.order_amount_range?.to,
        "filter[outlet_id]": selectedOutletId,
        "filter[customer_id]": state?.customer_id || undefined,
      }

      // Add filter[status][] for each selected status
      if (filterStatus.size > 0) {
        params['filter[status][]'] = Array.from(filterStatus)
      }

      // Add search query if present
      if (searchQuery) {
        params.search = searchQuery
      }

      const response = await merchantApi.orders.list(params)
      const rows = unwrapList(response).map(normalizeOrder)
      const openStatusList = response?.meta?.tabs?.find(tab => tab.key === 'open')?.filter?.status || []
      const closedStatusList = response?.meta?.tabs?.find(tab => tab.key === 'closed')?.filter?.status || []

      setOpenStatus(openStatusList)
      setClosedStatus(closedStatusList)

      setOrders(rows.length > 0 ? rows : [])

      // Update meta with full response (success/message/data/meta)
      setMeta(response || {})
    } catch (error) {
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {

    try {
      const response = await merchantApi.auth.me()
      if (!response?.data?.permissions?.includes(`view-${moduleCode}`)) {
        navigate('/unauthorized')
        return
      }
      const generalInfo = await merchantApi.general.systemInfo()
      setUserInfo(response?.data || null);
      setOrderTypes(generalInfo?.data?.order_types || []);
      setCancelable(response?.data?.permissions?.includes(`cancel-${moduleCode}`))
      setExportable(response?.data?.permissions?.includes(`export-${moduleCode}`))
      setSelectedOutletId(response?.data?.default_outlet?.id || null)
    } catch (err) {
      setUserInfo(null)
    } finally {
      setLoading(false)
    }
  }

  const loadOrderSetting = async () => {
    try {
      const response = await merchantApi.orders.showSetting()
      setAutoAcceptOrders(response?.data?.auto_accept_orders || false);
    } catch (err) {
      setAutoAcceptOrders(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
    loadOrderSetting()
  }, [])

  useEffect(() => {
    if (id) {
      setOpenMenuId(null)
      fetchOrderDetails(id)
      setShowOrderDetails(true)
    }
  }, [id])

  useEffect(() => {
    if (selectedOutletId) {
      loadOrders()
    }

  }, [filterValue?.type, filterValue?.date_range, filterValue?.custom_date_range, filterStatus, currentPage, selectedOutletId])

  const handleFilterReset = () => {
    setFilterValue({ custom_date_range: { start: null, end: null }, date_range: '' })
    setFilterStatus(new Set())
    setSearchQuery('')
    setCurrentPage(1)

    if (location.state) {
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null })
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (currentPage === 1) {
        loadOrders()
      } else {
        setCurrentPage(1)
      }
    }, 350)

    return () => clearTimeout(timeout)
  }, [searchQuery])

  // ── Derived filtered list ─────────────────────────────────────────────────
  // Use meta.tabs counts if available, else fallback to local calculation
  console.log("meta", meta?.meta?.tabs?.find(tab => tab.key === 'open'))
  const tabCounts = {
    all: meta?.meta?.tabs?.find(tab => tab.key === 'all')?.order_count || 0,
    open: meta?.meta?.tabs?.find(tab => tab.key === 'open')?.order_count || 0,
    closed: meta?.meta?.tabs?.find(tab => tab.key === 'closed')?.order_count || 0,
    pending: meta?.meta?.tabs?.find(tab => tab.key === 'pending')?.order_count || 0,
    submitted: meta?.meta?.tabs?.find(tab => tab.key === 'submitted')?.order_count || 0,
    paid: meta?.meta?.tabs?.find(tab => tab.key === 'paid')?.order_count || 0,
    confirmed: meta?.meta?.tabs?.find(tab => tab.key === 'confirmed')?.order_count || 0,
    completed: meta?.meta?.tabs?.find(tab => tab.key === 'completed')?.order_count || 0,
    ready: meta?.meta?.tabs?.find(tab => tab.key === 'ready')?.order_count || 0,
    cancelled: meta?.meta?.tabs?.find(tab => tab.key === 'cancelled')?.order_count || 0,
  }

  const filteredOrders = orders

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    if (tab === 'open') {
      setFilterStatus(new Set(openStatus.map(item => item)))
    } else if (tab === 'closed') {
      setFilterStatus(new Set(closedStatus.map(item => item)))
    } else {
      setFilterStatus(tab === 'all' ? new Set() : new Set([tab]))
    }
    setCurrentPage(1)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [filterValue?.type, filterValue?.date_range, customRange, filterStatus, searchQuery])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.orders-table__actions')) setOpenMenuId(null)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (selectedOrder) {
      const updated = orders.find((o) => o.id === selectedOrder.id)
      if (updated) setSelectedOrder(updated)
    }
  }, [orders])

  const handleToggleMenu = (e, orderId) => {
    e.stopPropagation()
    if (openMenuId === orderId) { setOpenMenuId(null); return }
    const rect = e.currentTarget.getBoundingClientRect()
    const top = (window.innerHeight - rect.bottom >= 170) ? rect.bottom + 8 : rect.top - 170 - 8
    const left = rect.right - 240
    setDropdownPos({ top, left })
    setOpenMenuId(orderId)
  }

  const fetchOrderDetails = async (orderId, initialOrder) => {
    try {
      setFetchingOrderDetails(true)
      // First set the initial order (from the list) so panel opens immediately
      if (initialOrder) {
        setSelectedOrder(initialOrder)
        setShowOrderDetails(true)
      }
      const response = await merchantApi.orders.show(orderId)
      const normalizedOrder = normalizeOrder(response?.data || {})
      setSelectedOrder(normalizedOrder)
    } catch (error) {
      console.error('Error fetching order details:', error)
    } finally {
      setFetchingOrderDetails(false)
    }
  }

  const nowTime = () => new Date().toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })

  const pushLog = (orderId, entry) =>
    setAuditLogs((prev) => ({ ...prev, [orderId]: [entry, ...(prev[orderId] || [])] }))

  const applyStatus = async (order, newStatus = 'pending', message, type = 'advance', reason = null, remark = null) => {
    const { action, payload } = getStatusPayload(order, newStatus, reason, remark)

    try {
      await merchantApi.orders[action](order?.rawId, payload)
    } catch (error) {
      console.error('Error applying status:', error)
    }
    await loadOrders()
  }

  const handleAdvanceStatus = async (order) => {
    const flow = STATUS_FLOW[order?.status]
    if (!flow?.next) return
    const snapshot = [...orders]
    undoSnapshot.current = { snapshot, orderId: order?.id, prevStatus: order?.status }
    try {
      await applyStatus(order, flow.next, `Status changed: "${order?.status}" → "${flow.next}"`, 'advance')
    } catch (error) {
      setOrders(snapshot)
      return
    }
    setToast({ message: `${order?.id} marked as ${flow.next}` })
  }

  const handleUndo = () => {
    if (!undoSnapshot.current) return
    const { snapshot, orderId, prevStatus } = undoSnapshot.current
    setOrders(snapshot)
    // pushLog(orderId, { message: `Status reverted to "${prevStatus}" via undo`, type: 'undo', time: nowTime() })
    setToast(null)
    undoSnapshot.current = null
  }

  const handleRevertConfirm = async (revertTo, reason) => {
    if (!selectedOrder) return

    try {
      await applyStatus(selectedOrder, revertTo, `Reverted: "${selectedOrder.status}" → pending`, 'revert', reason)
      setShowRevertModal(false)
    } catch (error) {
      return
    }
  }

  const handleCancelConfirm = async (reason, note) => {
    if (!selectedOrder) return

    try {
      await applyStatus(selectedOrder, 'cancelled', `Order cancelled — ${reason}${note ? `: "${note}"` : ''}`, 'cancel', reason, note)
      setShowCancelModal(false)
    } catch (error) {
      return
    }
  }

  const handleSaveSettings = async () => {
    setLoading(true)
    try {
      await merchantApi.orders.updateSetting({ auto_accept_orders: autoAcceptOrders })
    } catch (error) {
      return
    }
    finally {
      setLoading(false)
      loadOrderSetting()
      setShowOrderSettings(false)
    }
  }

  const handleExportCSV = async () => {
    if (exportingCsv) return

    setExportingCsv(true)

    const params = {
      "filter[status][]": filterStatus.size > 0 ? Array.from(filterStatus) : undefined,
      "filter[order_type]": filterValue?.type === 'all' ? undefined : filterValue?.type,
      "filter[date_range]": filterValue?.date_range === 'custom' || filterValue?.date_range === 'all' ? undefined : filterValue?.date_range,
      "filter[start_date]": filterValue?.custom_date_range?.start ? moment(filterValue.custom_date_range.start).format('YYYY-MM-DD') : undefined,
      "filter[end_date]": filterValue?.custom_date_range?.end ? moment(filterValue.custom_date_range.end).format('YYYY-MM-DD') : undefined,
    }
    try {
      const response = await merchantApi.orders.export(params)
      const byteCharacters = atob(response?.data?.base64);
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: response?.data?.mime_type || 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = response?.data?.file_name || 'customers.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (error) {
      return
    }
    finally {
      setExportingCsv(false)
    }
  }

  const handlePrintReceipt = async (orderId) => {
    if (!orderId || printingReceiptId) return

    setPrintingReceiptId(orderId)
    const token = localStorage.getItem('token')
    const endPoint = process.env.REACT_APP_MERCHANT_API_BASE_URL || 'https://manage.cuscapilab.com/merchant-api/v1'
    try {
      const response = await fetch(`${endPoint}/orders/${orderId}/receipt`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();

      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, '_blank');

    } catch (error) {
      console.error('Failed to fetch pdf', error);
    } finally {
      setPrintingReceiptId(null)
    }
  }

  const onPageChange = (page) => {
    setCurrentPage(page)
  }

  const columns = [
    {
      field: 'order_id', header: 'Order ID',
      render: (row) => (
        <div className="orders-table__pos-id">
          {row.id}
          {row.pos_id && (
            <>
              <span className="badge badge--primary">{row.pos_id}</span>
            </>
          )}
        </div>
      ),
    },
    {
      field: 'items', header: 'Items', minWidth: 200,
      render: (row) => (
        <div className="orders-table__items">
          {row.items.map((i) => `${i.name} x ${i.qty}`).join(', ')}
        </div>
      )
    },
    {
      field: 'type', header: 'Type',
      render: (row) => (<span className={`orders-tag odp__badge odp__badge--type-${row.type.toLowerCase().replace(/\s+/g, '')}`}> <Icon icon="ph:circle-fill" height={5} /> {row.type ? row.type[0].toUpperCase() + row.type.slice(1) : ''}</span>)
    },
    { field: 'total', header: 'Total', render: (row) => `RM${row.total.toFixed(2)}` },
    { field: 'placed', header: 'Placed', minWidth: 150 },
    { field: 'closed', header: 'Closed', minWidth: 150 },
    {
      field: 'status', header: 'Status',
      render: (row) => (
        <span className={`orders-table__status orders-table__status--${row.status}`}>
          <Icon icon="ph:circle-fill" height={5} />
          {formatStatusLabel(row.status)}
        </span>
      ),
    },
    {
      field: 'actions', header: '',
      render: (row) => {
        const isStatusOpen = openStatus.includes(row.status)
        const defaultActions = [
          {
            label: 'View details',
            onClick: (e) => {
              e.stopPropagation();
              setOpenMenuId(null);
              fetchOrderDetails(row.rawId, row);
            },
            icon: "ph:clipboard"
          },
          {
            label: 'Print receipt',
            onClick: (e) => { e.stopPropagation(); setOpenMenuId(null); handlePrintReceipt(row.rawId) },
            icon: "ph:receipt"
          },
        ]

        const cancelAction = {
          label: 'Cancel Order',
          permission: cancelable,
          onClick: (e) => { e.stopPropagation(); setShowCancelModal(true); setOpenMenuId(null); setSelectedOrder(row) },
          icon: "ph:minus-circle"
        }
        const actionButton = {
          paid: [
            {
              label: 'Confirm order',
              onClick: (e) => { e.stopPropagation(); setOpenMenuId(null); handleAdvanceStatus(row) },
              icon: "ph:check-circle"
            }
          ],
          confirmed: [
            {
              label: 'Mark as ready',
              onClick: (e) => { e.stopPropagation(); setOpenMenuId(null); handleAdvanceStatus(row) },
              icon: "ph:check-circle"
            },
            {
              label: 'Revert',
              onClick: (e) => { e.stopPropagation(); setOpenMenuId(null); setShowRevertModal(true) },
              icon: "ph:arrow-counter-clockwise"
            }
          ],
          ready: [
            {
              label: 'Revert',
              onClick: (e) => { e.stopPropagation(); setOpenMenuId(null); setShowRevertModal(true) },
              icon: "ph:arrow-counter-clockwise"
            }
          ],
          completed: [
            {
              label: 'Revert',
              onClick: (e) => { e.stopPropagation(); setOpenMenuId(null); setShowRevertModal(true) },
              icon: "ph:arrow-counter-clockwise"
            }
          ]
        }

        const actions = [...defaultActions, ...(actionButton?.[row?.status] || [])]
        if ((row?.can_cancel) && cancelable) actions.push(cancelAction)
        return (
          <div className="orders-table__actions">
            <button className="orders-table__menu-button" onClick={(e) => handleToggleMenu(e, row.id)}>
              <Icon icon="ph:dots-three-vertical" height={18} />
            </button>
            {openMenuId === row.id && (
              <div className="orders-table__dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
                {
                  actions.filter((item) => item?.permission !== false).map((item) => (
                    <button className="orders-table__dropdown-item" onClick={item.onClick}>
                      <Icon icon={item.icon} width={18} height={18} />
                      {item.label}
                    </button>
                  ))
                }
              </div>
            )}
          </div>
        )
      }
    }
  ]
  const canRevert = selectedOrder && !PAYMENT_LOCKED.has(selectedOrder?.status) && !!REVERT_MAP[selectedOrder?.status]

  return (
    <div className="orders-page">
      <Header
        items={[
          { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
          { label: 'Orders', current: true },
        ]}
      />
      <div className="orders-page__content">

        {/* HEADER */}
        <div className="orders-header">
          <div>
            <h1 className="orders-header__title">Orders</h1>
            <p className="orders-header__description">Auto-accept active — orders confirmed instantly</p>
          </div>
          <div className="orders-header__actions">
            {
              settingViewable && (
                <button className="orders-btn" onClick={() => setShowOrderSettings(true)}>
                  <Icon icon={'ph:gear'} height={18} /> Order settings
                </button>
              )
            }
            {
              exportable && (
                <button className="orders-btn" onClick={() => handleExportCSV()}>
                  <Icon icon={'ph:download-simple'} height={18} /> Export CSV
                </button>
              )
            }
            {showOrderSettings && (
              <div className="orders-settings-modal-overlay">
                <div className="orders-settings-modal">
                  <div className="orders-settings-modal__header">
                    <div>Order settings</div>
                    <button className="orders-settings-modal__close" onClick={() => setShowOrderSettings(false)}><Icon icon="ph:x" width={20} /></button>
                  </div>
                  <div className="orders-settings-modal__body">
                    <div className="orders-settings-modal__row">
                      <div>
                        <h4>Auto-accept orders</h4>
                        <p>Orders confirmed automatically without manual approval</p>
                      </div>
                      <label className="orders-switch">
                        <input type="checkbox" checked={autoAcceptOrders} onChange={(e) => setAutoAcceptOrders(e.target.checked)} />
                        <span className="orders-switch__slider"></span>
                      </label>
                    </div>
                  </div>
                  <div className="orders-settings-modal__footer">
                    <button className="orders-settings-modal__cancel" onClick={() => setShowOrderSettings(false)}>Cancel</button>
                    <button className="orders-settings-modal__save" onClick={() => handleSaveSettings()}>Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* STATS */}
        <div className="orders-stats">
          {[
            { label: 'OPEN ORDERS', value: meta?.meta?.summary?.open_orders },
            { label: 'CLOSED TODAY', value: meta?.meta?.summary?.closed_today },
            { label: 'TOTAL ORDERS TODAY', value: meta?.meta?.summary?.total_orders_today },
            { label: 'REVENUE TODAY', value: `RM ${(meta?.meta?.summary?.revenue_today || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
          ].map((stat) => (
            <div className="orders-stat-card" key={stat.label}>
              <div className="orders-stat-card__label">{stat.label}</div>
              <div className="orders-stat-card__value">{stat.value}</div>
            </div>
          ))}
        </div>

        {/* TABS */}
        <div className="orders-tabs">
          {ORDER_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={activeTab === tab.value ? 'active' : ''}
              onClick={() => handleTabChange(tab.value)}
            >
              {tab.label} ({tabCounts[tab.value]})
            </button>
          ))}
        </div>

        {/* FILTERS */}
        <div className="orders-toolbar">
          <div className="orders-toolbar__filters">
            <FilterDropdown
              label="Type"
              value={filterValue?.type}
              options={orderTypes}
              onChange={(value) => setFilterValue({ ...filterValue, type: value })}
            />
            <DateRangeDropdown
              value={filterValue?.date_range}
              customRange={filterValue?.custom_date_range}
              onChange={(value) => setFilterValue((prev) => ({ ...prev, date_range: value }))}
              onCustomRange={(value) => setFilterValue((prev) => ({ ...prev, custom_date_range: value, date_range: 'custom' }))}
            />
            <button className="customers-page__filter-btn" onClick={() => setFilterOpen(true)}>
              <Icon icon={'ph:funnel-simple'} className='customers-page__filter-icon' />Filters
            </button>
          </div>
          <div className="orders-search">
            <Icon icon="ph:magnifying-glass" height={18} />
            <input type="text" placeholder="Search orders" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>

        {/* TABLE */}
        <AppTable
          columns={columns}
          rows={filteredOrders || []}
          pagination={meta?.data || {}}
          loading={loading}
          onRowClick={(row) => {
            setOpenMenuId(null);
            fetchOrderDetails(row.rawId, row);
          }}
          onPageChange={(page) => {
            onPageChange(page)
          }}
        />
      </div>

      {/* ORDER DETAILS PANEL */}
      {showOrderDetails && selectedOrder && (
        <OrderDetailsPanel
          order={selectedOrder}
          auditLogs={auditLogs[selectedOrder.id]}
          onClose={() => setShowOrderDetails(false)}
          onAdvance={() => handleAdvanceStatus(selectedOrder)}
          onRevert={() => setShowRevertModal(true)}
          onCancel={() => setShowCancelModal(true)}
          canRevert={canRevert}
          loading={fetchingOrderDetails}
          cancelable={cancelable}
        />
      )}

      <Backdrop
        open={Boolean(printingReceiptId) || exportingCsv}
        sx={{ color: '#fff', zIndex: 2000, flexDirection: 'column', gap: 2 }}
      >
        <CircularProgress color="inherit" />
        <span>{exportingCsv ? 'Preparing CSV export...' : 'Preparing receipt...'}</span>
      </Backdrop>

      {/* CANCEL MODAL */}
      {showCancelModal && selectedOrder && (
        <CancelOrderModal
          order={selectedOrder}
          onConfirm={handleCancelConfirm}
          onClose={() => setShowCancelModal(false)}
        />
      )}

      {/* REVERT MODAL */}
      {showRevertModal && (
        <RevertModal order={selectedOrder} onConfirm={handleRevertConfirm} onCancel={() => setShowRevertModal(false)} />
      )}

      {/* UNDO TOAST */}
      <UndoToast toast={toast} onUndo={handleUndo} onDismiss={() => setToast(null)} />

      <OrderFilter
        open={filterOpen}
        setOpen={setFilterOpen}
        values={filterValue}
        setValues={setFilterValue}
        applyFilters={loadOrders}
        onReset={handleFilterReset}
        orderTypes={orderTypes}
      />
    </div>
  )
}
